import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Button,
  ButtonGroup,
  Tooltip,
  TablePagination,
  IconButton,
  Collapse,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  UnfoldMore as UnfoldMoreIcon,
} from "@mui/icons-material";
import { getAllTasksByQuarter } from "../../Action/api";
import "./styles.css";

const AllTask = () => {
    // Helper function to get current quarter based on month
  const getCurrentQuarter = () => {
    const month = new Date().getMonth();
    if (month >= 0 && month <= 2) return "Q1";
    if (month >= 3 && month <= 5) return "Q2";
    if (month >= 6 && month <= 8) return "Q3";
    return "Q4";
  };

  // Helper function to get current year
  const getCurrentYear = () => {
    return new Date().getFullYear().toString();
  };

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(getCurrentYear());
  const [selectedQuarter, setSelectedQuarter] = useState(getCurrentQuarter());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [responseData, setResponseData] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [sortBy, setSortBy] = useState('dueDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedAssigned, setSelectedAssigned] = useState('All');
  const [selectedStatus, setSelectedStatus] = useState('All');

  const quarters = ["Q1", "Q2", "Q3", "Q4"];
  // Dynamically generate years array: previous year to current year + 2
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 4 }, (_, i) => currentYear - 1 + i);

  useEffect(() => {
    fetchTasks();
  }, [selectedYear, selectedQuarter]);

  const fetchTasks = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAllTasksByQuarter(selectedYear, selectedQuarter);
      if (response.status) {
        setTasks(response.tasks || []);
        setResponseData(response);
      } else {
        setError(response.message || "Failed to fetch tasks");
        setTasks([]);
      }
    } catch (err) {
      setError(err.message || "An error occurred while fetching tasks");
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExpandRow = (taskId) => {
    setExpandedRow(expandedRow === taskId ? null : taskId);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "done":
        return "success";
      case "to do":
        return "default";
      case "in progress":
        return "primary";
      default:
        return "default";
    }
  };

  const getTaskTypeColor = (taskType) => {
    switch (taskType?.toLowerCase()) {
      case "feature":
        return "info";
      case "bug":
        return "error";
      case "improvements":
        return "warning";
      default:
        return "default";
    }
  };

  const getPriorityColor = (priority) => {
    if (priority?.toLowerCase().includes("high")) {
      return "#d32f2f";
    } else if (priority?.toLowerCase().includes("medium")) {
      return "#f57c00";
    } else if (priority?.toLowerCase().includes("low")) {
      return "#388e3c";
    }
    return "#757575";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getPointsDisplay = (points) => {
    if (points === 0) return "On Time";
    if (points > 0) return `+${points} (Early)`;
    return `${points} (Late)`;
  };

  const getPointsColor = (points) => {
    if (points === 0) return "success";
    if (points > 0) return "info";
    return "error";
  };

  // Calculate overdue days for incomplete tasks
  const calculateOverdueDays = (dueDate, status) => {
    if (status?.toLowerCase() === "done") return null;
    
    const due = new Date(dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    due.setHours(0, 0, 0, 0);
    
    const diffTime = today - due;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays > 0 ? diffDays : null;
  };

  const getTaskPointsDisplay = (task) => {
    const overdueDays = calculateOverdueDays(task.DueDate, task.Status);
    
    if (overdueDays !== null) {
      return {
        display: `-${overdueDays} (Overdue)`,
        isOverdue: true,
        color: "error"
      };
    }
    
    return {
      display: getPointsDisplay(task.Points),
      isOverdue: false,
      color: getPointsColor(task.Points)
    };
  };

  // Get unique values for filters
  const uniqueAssigned = ['All', ...new Set(tasks.map(task => task.assignedName).filter(Boolean))];
  const uniqueStatuses = ['All', ...new Set(tasks.map(task => task.Status).filter(Boolean))];

  // Apply filters
  const filteredTasks = tasks.filter(task => {
    const assignedMatch = selectedAssigned === 'All' || task.assignedName === selectedAssigned;
    const statusMatch = selectedStatus === 'All' || task.Status === selectedStatus;
    return assignedMatch && statusMatch;
  });

  // Calculate statistics
  const calculateStats = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const stats = {
      total: filteredTasks.length,
      byStatus: {},
      byPriority: {},
      byPerson: {},
      completionRate: 0,
      dueToday: 0,
      overdue: 0,
      completedOnTime: 0,
      completedLate: 0,
      performanceByPerson: {}
    };
    
    filteredTasks.forEach(task => {
      // Count by status
      const status = task.Status || 'Unknown';
      stats.byStatus[status] = (stats.byStatus[status] || 0) + 1;
      
      // Count by priority
      const priority = task.Priority || 'Unknown';
      stats.byPriority[priority] = (stats.byPriority[priority] || 0) + 1;
      
      // Count by person
      const person = task.assignedName || 'Unassigned';
      stats.byPerson[person] = (stats.byPerson[person] || 0) + 1;
      
      // Check due date status
      const dueDate = new Date(task.DueDate);
      dueDate.setHours(0, 0, 0, 0);
      
      const isDone = status.toLowerCase() === 'done';
      const daysDiff = Math.ceil((today - dueDate) / (1000 * 60 * 60 * 24));
      
      // Due today
      if (daysDiff === 0 && !isDone) {
        stats.dueToday++;
      }
      
      // Overdue (past due date and not done)
      if (daysDiff > 0 && !isDone) {
        stats.overdue++;
      }
      
      // Performance tracking
      if (isDone) {
        const actualDoneDate = task.ActualDoneDate ? new Date(task.ActualDoneDate) : null;
        if (actualDoneDate) {
          actualDoneDate.setHours(0, 0, 0, 0);
          const completionDiff = Math.ceil((actualDoneDate - dueDate) / (1000 * 60 * 60 * 24));
          
          if (completionDiff <= 0) {
            stats.completedOnTime++;
          } else {
            stats.completedLate++;
          }
          
          // Track per person
          if (!stats.performanceByPerson[person]) {
            stats.performanceByPerson[person] = {
              total: 0,
              onTime: 0,
              late: 0,
              overdue: 0
            };
          }
          
          stats.performanceByPerson[person].total++;
          if (completionDiff <= 0) {
            stats.performanceByPerson[person].onTime++;
          } else {
            stats.performanceByPerson[person].late++;
          }
        }
      } else if (daysDiff > 0) {
        // Track overdue per person
        if (!stats.performanceByPerson[person]) {
          stats.performanceByPerson[person] = {
            total: 0,
            onTime: 0,
            late: 0,
            overdue: 0
          };
        }
        stats.performanceByPerson[person].overdue++;
      }
    });
    
    // Calculate completion rate
    const doneCount = stats.byStatus['Done'] || 0;
    stats.completionRate = stats.total > 0 ? Math.round((doneCount / stats.total) * 100) : 0;
    
    return stats;
  };
  
  const stats = calculateStats();
  
  // Get top 5 contributors
  const topContributors = Object.entries(stats.byPerson)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5);

  const sortedTasks = [...filteredTasks].sort((a, b) => {
    if (!sortBy) return 0;
    
    let aValue, bValue;
    
    if (sortBy === 'status') {
      aValue = a.Status?.toLowerCase() || '';
      bValue = b.Status?.toLowerCase() || '';
    } else if (sortBy === 'priority') {
      const priorityOrder = { 'high': 3, 'medium': 2, 'low': 1 };
      aValue = priorityOrder[a.Priority?.toLowerCase()] || 0;
      bValue = priorityOrder[b.Priority?.toLowerCase()] || 0;
    } else if (sortBy === 'dueDate') {
      aValue = a.DueDate ? new Date(a.DueDate).getTime() : 0;
      bValue = b.DueDate ? new Date(b.DueDate).getTime() : 0;
    }
    
    if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const paginatedTasks = sortedTasks.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ 
      p: 3, 
      background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #228B22 100%)",
      minHeight: "100vh",
      position: "relative",
      overflow: "hidden",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "url('data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')",
        opacity: 0.3,
        animation: "backgroundScroll 20s linear infinite"
      }
    }}>
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4, 
          mb: 3, 
          borderRadius: 4,
          background: "rgba(255, 255, 255, 0.1)",
          backdropFilter: "blur(20px) saturate(180%)",
          border: "1px solid rgba(255, 255, 255, 0.2)",
          boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.37)",
          position: "relative",
          zIndex: 1
        }}
      >
        {/* Page Header */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: { xs: 1, sm: 2 }, mb: 1, flexWrap: "wrap" }}>
            <Typography 
              variant="h2" 
              sx={{ 
                fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                textShadow: "0 0 20px rgba(255,255,255,0.8)"
              }}
            >
              📊
            </Typography>
            <Typography 
              variant="h2" 
              sx={{ 
                fontWeight: 800,
                color: "#fff",
                textAlign: "center",
                textShadow: "0 0 20px rgba(255,255,255,0.5), 0 0 40px rgba(34, 139, 34, 0.4)",
                letterSpacing: { xs: "0.5px", sm: "1px", md: "2px" },
                fontSize: { xs: "1.25rem", sm: "1.75rem", md: "2.5rem" },
                background: "linear-gradient(45deg, #fff 30%, #d4f4dd 70%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent"
              }}
            >
              All Tasks Management Dashboard
            </Typography>
          </Box>
          <Typography
            variant="subtitle1"
            sx={{
              color: "rgba(255,255,255,0.8)",
              textAlign: "center",
              fontWeight: 500,
              mb: 0,
              fontSize: { xs: "0.875rem", sm: "1rem" },
              px: { xs: 2, sm: 0 }
            }}
          >
            Real-time insights and team performance analytics
          </Typography>
        </Box>

        {/* Filter Section - Moved to top */}
        <Box sx={{ 
          mb: 4, 
          display: "flex", 
          gap: { xs: 2, sm: 3 }, 
          alignItems: { xs: "stretch", md: "center" }, 
          flexWrap: "wrap",
          flexDirection: { xs: "column", md: "row" },
          background: "rgba(255, 255, 255, 0.08)",
          backdropFilter: "blur(10px)",
          p: { xs: 2, sm: 3 },
          borderRadius: 3,
          border: "1px solid rgba(255, 255, 255, 0.15)",
          boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.2)"
        }}>
          <Box sx={{ 
            background: "rgba(255, 255, 255, 0.95)", 
            p: { xs: 2, sm: 2.5 }, 
            borderRadius: 3, 
            boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.3)",
            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            width: { xs: "100%", md: "auto" },
            "&:hover": {
              transform: { xs: "none", md: "translateY(-5px) scale(1.02)" },
              boxShadow: "0 16px 32px rgba(34, 139, 34, 0.3)"
            }
          }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: "#228B22", fontSize: { xs: "0.8rem", sm: "0.9rem" } }}>
              📅 Select Year
            </Typography>
            <ButtonGroup variant="outlined" size="small" sx={{ boxShadow: "0 4px 12px rgba(0,0,0,0.08)", width: { xs: "100%", sm: "auto" } }}>
              {years.map((year) => (
                <Button
                  key={year}
                  variant={selectedYear === year.toString() ? "contained" : "outlined"}
                  onClick={() => setSelectedYear(year.toString())}
                  sx={{
                    background: selectedYear === year.toString() 
                      ? "linear-gradient(135deg, #228B22 0%, #32CD32 100%)" 
                      : "rgba(255, 255, 255, 0.9)",
                    border: "2px solid",
                    borderColor: selectedYear === year.toString() ? "#228B22" : "rgba(34, 139, 34, 0.3)",
                    color: selectedYear === year.toString() ? "#fff" : "#228B22",
                    fontWeight: 700,
                    px: { xs: 2, sm: 3 },
                    fontSize: { xs: "0.8rem", sm: "0.95rem" },
                    flex: { xs: 1, sm: "none" },
                    transition: "all 0.3s ease",
                    "&:hover": {
                      borderColor: "#228B22",
                      background: selectedYear === year.toString() 
                        ? "linear-gradient(135deg, #32CD32 0%, #228B22 100%)" 
                        : "rgba(34, 139, 34, 0.1)",
                      transform: "scale(1.05)",
                      boxShadow: "0 4px 12px rgba(34, 139, 34, 0.3)"
                    }
                  }}
                >
                  {year}
                </Button>
              ))}
            </ButtonGroup>
          </Box>

          <Box sx={{ 
            background: "rgba(255, 255, 255, 0.95)", 
            p: { xs: 2, sm: 2.5 }, 
            borderRadius: 3, 
            boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.3)",
            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            width: { xs: "100%", md: "auto" },
            "&:hover": {
              transform: { xs: "none", md: "translateY(-5px) scale(1.02)" },
              boxShadow: "0 16px 32px rgba(34, 139, 34, 0.3)"
            }
          }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: "#228B22", fontSize: { xs: "0.8rem", sm: "0.9rem" } }}>
              📊 Select Quarter
            </Typography>
            <ButtonGroup variant="outlined" size="small" sx={{ boxShadow: "0 4px 12px rgba(0,0,0,0.08)", width: { xs: "100%", sm: "auto" } }}>
              {quarters.map((quarter) => (
                <Button
                  key={quarter}
                  variant={selectedQuarter === quarter ? "contained" : "outlined"}
                  onClick={() => setSelectedQuarter(quarter)}
                  sx={{
                    background: selectedQuarter === quarter 
                      ? "linear-gradient(135deg, #228B22 0%, #32CD32 100%)" 
                      : "rgba(255, 255, 255, 0.9)",
                    border: "2px solid",
                    borderColor: selectedQuarter === quarter ? "#228B22" : "rgba(34, 139, 34, 0.3)",
                    color: selectedQuarter === quarter ? "#fff" : "#228B22",
                    fontWeight: 700,
                    px: { xs: 2, sm: 3 },
                    fontSize: { xs: "0.8rem", sm: "0.95rem" },
                    flex: { xs: 1, sm: "none" },
                    transition: "all 0.3s ease",
                    "&:hover": {
                      borderColor: "#228B22",
                      background: selectedQuarter === quarter 
                        ? "linear-gradient(135deg, #32CD32 0%, #228B22 100%)" 
                        : "rgba(34, 139, 34, 0.1)",
                      transform: "scale(1.05)",
                      boxShadow: "0 4px 12px rgba(34, 139, 34, 0.3)"
                    }
                  }}
                >
                  {quarter}
                </Button>
              ))}
            </ButtonGroup>
          </Box>

          <Box sx={{ 
            background: "rgba(255, 255, 255, 0.95)", 
            p: { xs: 2, sm: 2.5 }, 
            borderRadius: 3, 
            boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.3)",
            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            minWidth: { xs: "100%", sm: 200 },
            width: { xs: "100%", md: "auto" },
            "&:hover": {
              transform: { xs: "none", md: "translateY(-5px) scale(1.02)" },
              boxShadow: "0 16px 32px rgba(34, 139, 34, 0.3)"
            }
          }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: "#228B22", fontSize: { xs: "0.8rem", sm: "0.9rem" } }}>
              👤 Filter by Assigned
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={selectedAssigned}
                onChange={(e) => setSelectedAssigned(e.target.value)}
                sx={{
                  borderRadius: '8px',
                  border: '2px solid rgba(34, 139, 34, 0.3)',
                  background: 'rgba(255, 255, 255, 0.9)',
                  color: '#228B22',
                  fontWeight: 700,
                  fontSize: { xs: '0.8rem', sm: '0.9rem' },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 300,
                      mt: 1,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                      borderRadius: 2,
                      '& .MuiMenuItem-root': {
                        fontSize: { xs: '0.85rem', sm: '0.9rem' },
                        py: 1.5,
                        '&:hover': {
                          backgroundColor: 'rgba(34, 139, 34, 0.1)'
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(34, 139, 34, 0.15)',
                          fontWeight: 700,
                          '&:hover': {
                            backgroundColor: 'rgba(34, 139, 34, 0.2)'
                          }
                        }
                      }
                    }
                  }
                }}
              >
                {uniqueAssigned.map((assigned) => (
                  <MenuItem key={assigned} value={assigned}>
                    {assigned}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ 
            background: "rgba(255, 255, 255, 0.95)", 
            p: { xs: 2, sm: 2.5 }, 
            borderRadius: 3, 
            boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.3)",
            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            minWidth: { xs: "100%", sm: 200 },
            width: { xs: "100%", md: "auto" },
            "&:hover": {
              transform: { xs: "none", md: "translateY(-5px) scale(1.02)" },
              boxShadow: "0 16px 32px rgba(34, 139, 34, 0.3)"
            }
          }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: "#228B22", fontSize: { xs: "0.8rem", sm: "0.9rem" } }}>
              📊 Filter by Status
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                sx={{
                  borderRadius: '8px',
                  border: '2px solid rgba(34, 139, 34, 0.3)',
                  background: 'rgba(255, 255, 255, 0.9)',
                  color: '#228B22',
                  fontWeight: 700,
                  fontSize: { xs: '0.8rem', sm: '0.9rem' },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 300,
                      mt: 1,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                      borderRadius: 2,
                      '& .MuiMenuItem-root': {
                        fontSize: { xs: '0.85rem', sm: '0.9rem' },
                        py: 1.5,
                        '&:hover': {
                          backgroundColor: 'rgba(34, 139, 34, 0.1)'
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(34, 139, 34, 0.15)',
                          fontWeight: 700,
                          '&:hover': {
                            backgroundColor: 'rgba(34, 139, 34, 0.2)'
                          }
                        }
                      }
                    }
                  }
                }}
              >
                {uniqueStatuses.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {responseData && (
            <Box sx={{ 
              ml: { xs: 0, md: "auto" },
              width: { xs: "100%", md: "auto" },
              background: "linear-gradient(135deg, #228B22 0%, #32CD32 50%, #3CB371 100%)",
              p: { xs: 2, sm: 3 },
              borderRadius: 3,
              color: "#fff",
              boxShadow: "0 12px 40px rgba(34, 139, 34, 0.5)",
              minWidth: { xs: "100%", sm: 240 },
              border: "1px solid rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(10px)",
              position: "relative",
              overflow: "hidden",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: { xs: "none", md: "translateY(-5px) scale(1.03)" },
                boxShadow: "0 16px 50px rgba(34, 139, 34, 0.6)"
              },
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "3px",
                background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent)",
                animation: "slideRight 2s infinite"
              }
            }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, opacity: 0.95, letterSpacing: "0.5px" }}>
                📆 {responseData.dateRange?.startDate} → {responseData.dateRange?.endDate}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, textShadow: "0 2px 10px rgba(0,0,0,0.3)" }}>
                📝 {filteredTasks.length} <Typography component="span" sx={{ fontSize: "0.9rem", fontWeight: 600 }}>Tasks</Typography>
              </Typography>
              {(selectedAssigned !== 'All' || selectedStatus !== 'All') && (
                <Typography variant="caption" sx={{ opacity: 0.9, display: 'block', mt: 0.5 }}>
                  (Filtered from {responseData.count})
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {/* Management Dashboard Statistics */}
        <Box sx={{ mb: 4 }}>

          {/* Statistics Cards Grid */}
          <Box sx={{ 
            display: "grid", 
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3, 1fr)", lg: "repeat(6, 1fr)" },
            gap: { xs: 2, sm: 3 },
            mb: 4
          }}>
            {/* Total Tasks Card */}
            <Box sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderRadius: 3,
              p: { xs: 2, sm: 3 },
              color: "#fff",
              boxShadow: "0 8px 32px rgba(102, 126, 234, 0.4)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(10px)",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: { xs: "none", sm: "translateY(-5px)" },
                boxShadow: "0 12px 40px rgba(102, 126, 234, 0.5)"
              }
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, opacity: 0.9 }}>
                📝 Total Tasks
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
                {stats.total}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Active in {selectedQuarter} {selectedYear}
              </Typography>
            </Box>

            {/* Completion Rate Card */}
            <Box sx={{
              background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
              borderRadius: 3,
              p: 3,
              color: "#fff",
              boxShadow: "0 8px 32px rgba(240, 147, 251, 0.4)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(10px)",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-5px)",
                boxShadow: "0 12px 40px rgba(240, 147, 251, 0.5)"
              }
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, opacity: 0.9 }}>
                ✅ Completion Rate
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
                {stats.completionRate}%
              </Typography>
              <Box sx={{ 
                width: "100%", 
                height: 8, 
                bgcolor: "rgba(255,255,255,0.3)", 
                borderRadius: 2,
                overflow: "hidden"
              }}>
                <Box sx={{
                  width: `${stats.completionRate}%`,
                  height: "100%",
                  bgcolor: "#fff",
                  transition: "width 1s ease"
                }} />
              </Box>
            </Box>

            {/* Due Today Card */}
            <Box sx={{
              background: "linear-gradient(135deg, #FFA500 0%, #FF8C00 100%)",
              borderRadius: 3,
              p: 3,
              color: "#fff",
              boxShadow: "0 8px 32px rgba(255, 165, 0, 0.4)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(10px)",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-5px)",
                boxShadow: "0 12px 40px rgba(255, 165, 0, 0.6)"
              }
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, opacity: 0.9 }}>
                ⏰ Due Today
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
                {stats.dueToday}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {stats.dueToday > 0 ? 'Action Required!' : 'All Clear'}
              </Typography>
            </Box>

            {/* Overdue Card */}
            <Box sx={{
              background: "linear-gradient(135deg, #ff6b6b 0%, #c92a2a 100%)",
              borderRadius: 3,
              p: 3,
              color: "#fff",
              boxShadow: "0 8px 32px rgba(255, 107, 107, 0.4)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(10px)",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-5px)",
                boxShadow: "0 12px 40px rgba(255, 107, 107, 0.6)"
              }
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, opacity: 0.9 }}>
                ⚠️ Overdue
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
                {stats.overdue}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {stats.overdue > 0 ? 'Needs Attention!' : 'No Delays'}
              </Typography>
            </Box>

            {/* Completed On Time Card */}
            <Box sx={{
              background: "linear-gradient(135deg, #51cf66 0%, #37b24d 100%)",
              borderRadius: 3,
              p: 3,
              color: "#fff",
              boxShadow: "0 8px 32px rgba(81, 207, 102, 0.4)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(10px)",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-5px)",
                boxShadow: "0 12px 40px rgba(81, 207, 102, 0.5)"
              }
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, opacity: 0.9 }}>
                ✨ On Time
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
                {stats.completedOnTime}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Completed before deadline
              </Typography>
            </Box>

            {/* Completed Late Card */}
            <Box sx={{
              background: "linear-gradient(135deg, #ffa94d 0%, #fd7e14 100%)",
              borderRadius: 3,
              p: 3,
              color: "#fff",
              boxShadow: "0 8px 32px rgba(255, 169, 77, 0.4)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(10px)",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-5px)",
                boxShadow: "0 12px 40px rgba(255, 169, 77, 0.5)"
              }
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, opacity: 0.9 }}>
                ⏱️ Late
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
                {stats.completedLate}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Completed after deadline
              </Typography>
            </Box>
          </Box>

          {/* Detailed Analytics Grid */}
          <Box sx={{ 
            display: "grid", 
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 3
          }}>
            {/* Priority Breakdown */}
            <Box sx={{
              background: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(20px)",
              borderRadius: 3,
              p: 3,
              border: "1px solid rgba(255, 255, 255, 0.2)",
              boxShadow: "0 8px 32px rgba(31, 38, 135, 0.3)"
            }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#fff", mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
                ⚡ Priority Distribution
              </Typography>
              {Object.entries(stats.byPriority).map(([priority, count]) => {
                const percentage = stats.total > 0 ? Math.round((count / stats.total) * 100) : 0;
                const color = priority.toLowerCase().includes('high') ? '#ef4444' : 
                             priority.toLowerCase().includes('medium') ? '#f59e0b' : '#10b981';
                return (
                  <Box key={priority} sx={{ mb: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography variant="body2" sx={{ color: "#fff", fontWeight: 600 }}>
                        {priority}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#fff", fontWeight: 600 }}>
                        {count} ({percentage}%)
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      width: "100%", 
                      height: 10, 
                      bgcolor: "rgba(255,255,255,0.2)", 
                      borderRadius: 2,
                      overflow: "hidden"
                    }}>
                      <Box sx={{
                        width: `${percentage}%`,
                        height: "100%",
                        bgcolor: color,
                        transition: "width 1s ease",
                        boxShadow: `0 0 10px ${color}`
                      }} />
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* Top Contributors */}
            <Box sx={{
              background: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(20px)",
              borderRadius: 3,
              p: 3,
              border: "1px solid rgba(255, 255, 255, 0.2)",
              boxShadow: "0 8px 32px rgba(31, 38, 135, 0.3)"
            }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#fff", mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
                👥 Top Contributors
              </Typography>
              {topContributors.map(([person, count], index) => {
                const percentage = stats.total > 0 ? Math.round((count / stats.total) * 100) : 0;
                const colors = ['#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6'];
                return (
                  <Box key={person} sx={{ mb: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{
                          width: 30,
                          height: 30,
                          borderRadius: "50%",
                          background: colors[index],
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          color: "#fff"
                        }}>
                          {index + 1}
                        </Box>
                        <Typography variant="body2" sx={{ color: "#fff", fontWeight: 600 }}>
                          {person}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: "#fff", fontWeight: 600 }}>
                        {count} ({percentage}%)
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      width: "100%", 
                      height: 8, 
                      bgcolor: "rgba(255,255,255,0.2)", 
                      borderRadius: 2,
                      overflow: "hidden"
                    }}>
                      <Box sx={{
                        width: `${percentage}%`,
                        height: "100%",
                        bgcolor: colors[index],
                        transition: "width 1s ease",
                        boxShadow: `0 0 10px ${colors[index]}`
                      }} />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error State */}
        {error && !loading && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Table */}
        {!loading && !error && tasks.length > 0 && (
          <>
            <TableContainer sx={{ 
              borderRadius: 3, 
              boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
              overflow: "auto",
              overflowX: { xs: "scroll", md: "auto" },
              background: "rgba(255, 255, 255, 0.98)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              WebkitOverflowScrolling: "touch",
              "&::-webkit-scrollbar": {
                height: "8px"
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#228B22",
                borderRadius: "4px"
              }
            }}>
              <Table sx={{ minWidth: { xs: 900, md: 650 } }} aria-label="tasks table">
                <TableHead>
                  <TableRow sx={{ 
                    background: "linear-gradient(135deg, #228B22 0%, #32CD32 50%, #3CB371 100%)",
                    boxShadow: "0 4px 12px rgba(34, 139, 34, 0.3)"
                  }}>
                    <TableCell sx={{ width: 50, borderBottom: "none", py: { xs: 1, sm: 2 } }} />
                    <TableCell sx={{ fontWeight: 800, color: "#fff", fontSize: { xs: "0.75rem", sm: "0.875rem" }, borderBottom: "none", py: { xs: 1, sm: 2 }, letterSpacing: "0.5px", whiteSpace: "nowrap" }}>🆔 TASK ID</TableCell>
                    <TableCell sx={{ fontWeight: 800, color: "#fff", fontSize: { xs: "0.75rem", sm: "0.875rem" }, borderBottom: "none", py: { xs: 1, sm: 2 }, letterSpacing: "0.5px", whiteSpace: "nowrap" }}>📌 TITLE</TableCell>
                    <TableCell sx={{ fontWeight: 800, color: "#fff", fontSize: { xs: "0.75rem", sm: "0.875rem" }, borderBottom: "none", py: { xs: 1, sm: 2 }, letterSpacing: "0.5px", whiteSpace: "nowrap" }}>👤 ASSIGNED</TableCell>
                    <TableCell 
                      onClick={() => handleSort('status')} 
                      sx={{ 
                        fontWeight: 800, 
                        color: "#fff", 
                        fontSize: "0.875rem", 
                        borderBottom: "none", 
                        py: 2, 
                        letterSpacing: "0.5px",
                        cursor: "pointer",
                        userSelect: "none",
                        "&:hover": { 
                          opacity: 0.9,
                          backgroundColor: "rgba(255, 255, 255, 0.1)"
                        }
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        📊 STATUS 
                        {sortBy === 'status' ? (
                          sortOrder === 'asc' ? 
                            <ArrowUpwardIcon sx={{ fontSize: "1.2rem", color: "#fff" }} /> : 
                            <ArrowDownwardIcon sx={{ fontSize: "1.2rem", color: "#fff" }} />
                        ) : (
                          <UnfoldMoreIcon sx={{ fontSize: "1.2rem", color: "rgba(255, 255, 255, 0.6)" }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 800, color: "#fff", fontSize: "0.875rem", borderBottom: "none", py: 2, letterSpacing: "0.5px" }}>🏷️ TYPE</TableCell>
                    <TableCell 
                      onClick={() => handleSort('priority')} 
                      sx={{ 
                        fontWeight: 800, 
                        color: "#fff", 
                        fontSize: "0.875rem", 
                        borderBottom: "none", 
                        py: 2, 
                        letterSpacing: "0.5px",
                        cursor: "pointer",
                        userSelect: "none",
                        "&:hover": { 
                          opacity: 0.9,
                          backgroundColor: "rgba(255, 255, 255, 0.1)"
                        }
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        ⚡ PRIORITY 
                        {sortBy === 'priority' ? (
                          sortOrder === 'asc' ? 
                            <ArrowUpwardIcon sx={{ fontSize: "1.2rem", color: "#fff" }} /> : 
                            <ArrowDownwardIcon sx={{ fontSize: "1.2rem", color: "#fff" }} />
                        ) : (
                          <UnfoldMoreIcon sx={{ fontSize: "1.2rem", color: "rgba(255, 255, 255, 0.6)" }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 800, color: "#fff", fontSize: "0.875rem", borderBottom: "none", py: 2, letterSpacing: "0.5px" }}>🚀 START</TableCell>
                    <TableCell 
                      onClick={() => handleSort('dueDate')} 
                      sx={{ 
                        fontWeight: 800, 
                        color: "#fff", 
                        fontSize: "0.875rem", 
                        borderBottom: "none", 
                        py: 2, 
                        letterSpacing: "0.5px",
                        cursor: "pointer",
                        userSelect: "none",
                        "&:hover": { 
                          opacity: 0.9,
                          backgroundColor: "rgba(255, 255, 255, 0.1)"
                        }
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        🎯 DUE
                        {sortBy === 'dueDate' ? (
                          sortOrder === 'asc' ? 
                            <ArrowUpwardIcon sx={{ fontSize: "1.2rem", color: "#fff" }} /> : 
                            <ArrowDownwardIcon sx={{ fontSize: "1.2rem", color: "#fff" }} />
                        ) : (
                          <UnfoldMoreIcon sx={{ fontSize: "1.2rem", color: "rgba(255, 255, 255, 0.6)" }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 800, color: "#fff", fontSize: "0.875rem", borderBottom: "none", py: 2, letterSpacing: "0.5px" }}>⏱️ DAYS</TableCell>
                    <TableCell sx={{ fontWeight: 800, color: "#fff", fontSize: "0.875rem", borderBottom: "none", py: 2, letterSpacing: "0.5px" }}>⭐ POINTS</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedTasks.map((task, index) => (
                    <React.Fragment key={task.TaskID}>
                      <TableRow
                        sx={{
                          backgroundColor: index % 2 === 0 ? "rgba(249, 250, 255, 0.5)" : "#fff",
                          borderLeft: "4px solid transparent",
                          "&:hover": { 
                            backgroundColor: "rgba(34, 139, 34, 0.08)",
                            borderLeft: "4px solid #228B22",
                            boxShadow: "0 4px 20px rgba(34, 139, 34, 0.15)",
                            "& .MuiTableCell-root": {
                              color: "#1f2937"
                            }
                          },
                          cursor: "pointer",
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        }}
                      >
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => handleExpandRow(task.TaskID)}
                          >
                            {expandedRow === task.TaskID ? (
                              <KeyboardArrowUpIcon />
                            ) : (
                              <KeyboardArrowDownIcon />
                            )}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={task.TaskID}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title={task.Title}>
                            <Typography
                              variant="body2"
                              sx={{
                                maxWidth: 200,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {task.Title}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{task.assignedName || "N/A"}</TableCell>
                        <TableCell>
                          <Chip
                            label={task.Status}
                            size="small"
                            color={getStatusColor(task.Status)}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={task.TaskType}
                            size="small"
                            color={getTaskTypeColor(task.TaskType)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              color: getPriorityColor(task.Priority),
                              fontWeight: "bold",
                              fontSize: "0.75rem",
                            }}
                          >
                            {task.Priority}
                          </Typography>
                        </TableCell>
                        <TableCell>{formatDate(task.StartDate)}</TableCell>
                        <TableCell>{formatDate(task.DueDate)}</TableCell>
                        <TableCell align="center">{task.TotalManDays}</TableCell>
                        <TableCell>
                          {(() => {
                            const pointsInfo = getTaskPointsDisplay(task);
                            return (
                              <Chip
                                label={pointsInfo.display}
                                size="small"
                                color={pointsInfo.color}
                                variant={pointsInfo.isOverdue ? "filled" : "outlined"}
                                sx={pointsInfo.isOverdue ? {
                                  backgroundColor: "#d32f2f",
                                  color: "#fff",
                                  fontWeight: 700,
                                  animation: "pulse 2s infinite",
                                  boxShadow: "0 0 10px rgba(211, 47, 47, 0.5)"
                                } : {}}
                              />
                            );
                          })()}
                        </TableCell>
                      </TableRow>

                      {/* Expanded Row for Details */}
                      <TableRow>
                        <TableCell
                          style={{ paddingBottom: 0, paddingTop: 0 }}
                          colSpan={11}
                        >
                          <Collapse
                            in={expandedRow === task.TaskID}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box sx={{ 
                              margin: 1, 
                              p: 2.5, 
                              background: "#f8f9fa", 
                              borderRadius: 2,
                              border: "1px solid #e0e0e0"
                            }}>
                              <Typography 
                                variant="subtitle2" 
                                sx={{ 
                                  mb: 1.5,
                                  fontWeight: 700,
                                  color: "#228B22",
                                  fontSize: "0.85rem",
                                  borderBottom: "2px solid #228B22",
                                  pb: 0.5,
                                  display: "inline-block"
                                }}
                              >
                                📋 Task Details
                              </Typography>
                              <Box sx={{ 
                                display: "grid", 
                                gridTemplateColumns: { xs: "1fr", sm: "auto 1fr", md: "auto 1fr auto 1fr" },
                                gap: 1,
                                alignItems: "start"
                              }}>
                                {/* Row 1 */}
                                <Typography variant="body2" sx={{ fontWeight: 700, color: "#555", fontSize: "0.8rem" }}>
                                  📝 Description:
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#374151", fontSize: "0.8rem", gridColumn: { xs: "1 / -1", sm: "2", md: "2 / -1" }, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                  {task.Description || "N/A"}
                                </Typography>

                                {/* Row 2 */}
                                <Typography variant="body2" sx={{ fontWeight: 700, color: "#555", fontSize: "0.8rem" }}>
                                  🎯 Expected Output:
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#374151", fontSize: "0.8rem", gridColumn: { xs: "1 / -1", sm: "2", md: "2 / -1" }, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                  {task.ExpectedOutput || "N/A"}
                                </Typography>

                                {/* Row 3 - Short fields in 4 columns */}
                                <Typography variant="body2" sx={{ fontWeight: 700, color: "#555", fontSize: "0.8rem" }}>
                                  👤 Assigned:
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#374151", fontSize: "0.8rem" }}>
                                  {task.assignedName || "N/A"}
                                </Typography>
                                
                                <Typography variant="body2" sx={{ fontWeight: 700, color: "#555", fontSize: "0.8rem" }}>
                                  👥 Reported By:
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#374151", fontSize: "0.8rem" }}>
                                  {task.ReportedBy || "N/A"}
                                </Typography>

                                {/* Row 4 */}
                                <Typography variant="body2" sx={{ fontWeight: 700, color: "#555", fontSize: "0.8rem" }}>
                                  📊 Status:
                                </Typography>
                                <Box>
                                  <Chip
                                    label={task.Status}
                                    size="small"
                                    color={getStatusColor(task.Status)}
                                    sx={{ height: 20, fontSize: "0.7rem" }}
                                  />
                                </Box>

                                <Typography variant="body2" sx={{ fontWeight: 700, color: "#555", fontSize: "0.8rem" }}>
                                  🏷️ Type:
                                </Typography>
                                <Box>
                                  <Chip
                                    label={task.TaskType}
                                    size="small"
                                    color={getTaskTypeColor(task.TaskType)}
                                    sx={{ height: 20, fontSize: "0.7rem" }}
                                  />
                                </Box>

                                {/* Row 5 */}
                                <Typography variant="body2" sx={{ fontWeight: 700, color: "#555", fontSize: "0.8rem" }}>
                                  ⚡ Priority:
                                </Typography>
                                <Typography variant="body2" sx={{ color: getPriorityColor(task.Priority), fontWeight: 700, fontSize: "0.8rem" }}>
                                  {task.Priority || "N/A"}
                                </Typography>

                                <Typography variant="body2" sx={{ fontWeight: 700, color: "#555", fontSize: "0.8rem" }}>
                                  ⏱️ Man Days:
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#374151", fontSize: "0.8rem", fontWeight: 600 }}>
                                  {task.TotalManDays || "N/A"}
                                </Typography>

                                {/* Row 6 */}
                                <Typography variant="body2" sx={{ fontWeight: 700, color: "#555", fontSize: "0.8rem" }}>
                                  🚀 Start Date:
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#374151", fontSize: "0.8rem" }}>
                                  {formatDate(task.StartDate)}
                                </Typography>

                                <Typography variant="body2" sx={{ fontWeight: 700, color: "#555", fontSize: "0.8rem" }}>
                                  🎯 Due Date:
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#374151", fontSize: "0.8rem" }}>
                                  {formatDate(task.DueDate)}
                                </Typography>

                                {/* Row 7 */}
                                <Typography variant="body2" sx={{ fontWeight: 700, color: "#555", fontSize: "0.8rem" }}>
                                  ✅ Done Date:
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#374151", fontSize: "0.8rem" }}>
                                  {formatDate(task.ActualDoneDate)}
                                </Typography>

                                <Typography variant="body2" sx={{ fontWeight: 700, color: "#555", fontSize: "0.8rem" }}>
                                  ⭐ Points:
                                </Typography>
                                <Box>
                                  {(() => {
                                    const pointsInfo = getTaskPointsDisplay(task);
                                    return (
                                      <Chip
                                        label={pointsInfo.display}
                                        size="small"
                                        color={pointsInfo.color}
                                        variant={pointsInfo.isOverdue ? "filled" : "outlined"}
                                        sx={{ height: 20, fontSize: "0.7rem" }}
                                      />
                                    );
                                  })()}
                                </Box>

                                {/* Row 8 */}
                                <Typography variant="body2" sx={{ fontWeight: 700, color: "#555", fontSize: "0.8rem" }}>
                                  🕐 Created:
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#374151", fontSize: "0.8rem" }}>
                                  {formatDate(task.CreatedAt)}
                                </Typography>

                                <Typography variant="body2" sx={{ fontWeight: 700, color: "#555", fontSize: "0.8rem" }}>
                                  🔄 Updated:
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#374151", fontSize: "0.8rem" }}>
                                  {formatDate(task.UpdatedAt)}
                                </Typography>
                              </Box>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              rowsPerPageOptions={[50, 100, 150]}
              component="div"
              count={filteredTasks.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}

        {/* No Data State */}
        {!loading && !error && filteredTasks.length === 0 && (
          <Box sx={{ 
            textAlign: "center", 
            p: { xs: 4, sm: 6, md: 10 },
            background: "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(10px)",
            borderRadius: 4,
            border: "2px dashed rgba(255, 255, 255, 0.3)",
            boxShadow: "0 8px 32px rgba(34, 139, 34, 0.2)"
          }}>
            <Box sx={{ 
              fontSize: { xs: "3rem", sm: "4rem", md: "5rem" }, 
              mb: 2,
              animation: "float 3s ease-in-out infinite",
              filter: "drop-shadow(0 0 20px rgba(34, 139, 34, 0.5))"
            }}>📭</Box>
            <Typography variant="h4" sx={{ 
              fontWeight: 800, 
              color: "#fff", 
              mb: 2,
              fontSize: { xs: "1.5rem", sm: "2rem", md: "2.125rem" },
              textShadow: "0 2px 10px rgba(0,0,0,0.3)",
              letterSpacing: "1px"
            }}>
              No Tasks Found
            </Typography>
            <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.8)", fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" } }}>
              There are no tasks for {selectedQuarter} {selectedYear}
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default AllTask;

